/* MARCA | 24 Sep 2026 | Bundle purchase options. One case keeps the theme add to cart flow. Two or three cases add the chosen products together. */
if (!customElements.get('purchase-options')) {
  class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.productInfo = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId) || this.productInfo?.querySelector('[data-product-form]');
      this.submitButton = this.form?.querySelector('[data-add-to-cart]');
      this.cartBarButton = this.productInfo?.querySelector('[data-cart-bar-add-to-cart]');
      this.variantInput = this.form?.querySelector('[name="id"]');
      this.quantityInput = this.form?.querySelector('[name="quantity"]');
      this.quantity = Math.max(1, Number(this.quantityInput?.value) || 1);
      this.adding = false;
      const quantityValue = this.querySelector('[data-qty-value]');
      if (quantityValue) quantityValue.textContent = String(this.quantity);
      this.fillPickers();
      this.unitPrice = Number(this.dataset.unitPrice) || 0;
      this.cansPerCase = Number(this.dataset.cans) || 12;
      this.maxQty = 10;
      this.currentVariantId = this.variantInput?.value || this.dataset.variantId || '';

      const variantData = this.querySelector('[data-purchase-variants]');
      this.variants = variantData ? JSON.parse(variantData.textContent) : [];

      this.onSubmit = (event) => this.handleSubmit(event);
      this.onClick = (event) => this.handleClick(event);
      this.onChange = (event) => this.handleChange(event);
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeMenus();
      };
      /* MARCA | 24 Sep 2026 | The sticky bar clicks the product button. Multi-case adds from here so that click still adds every selected case. */
      this.onCartBarClick = (event) => {
        if (this.selectedCount() <= 1) return;
        event.preventDefault();
        event.stopPropagation();
        this.handleSubmit(event);
      };
      this.onProductChange = () => this.syncVariantFromForm();

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      this.cartBarButton?.addEventListener("click", this.onCartBarClick, true);
      this.productInfo?.addEventListener('change', this.onProductChange);
      this.form?.addEventListener('submit', this.onSubmit, true);

      if (this.variantInput) {
        this.variantObserver = new MutationObserver(() => this.syncVariantFromForm());
        this.variantObserver.observe(this.variantInput, { attributes: true, attributeFilter: ['value', 'disabled'] });
      }

      /* MARCA | 25 Sep 2026 | The theme can re-enable add to cart after a variant change. Keep it locked while cases are missing. */
      if (this.submitButton) {
        this.buttonObserver = new MutationObserver(() => {
          if (this.locked && this.submitButton && !this.submitButton.disabled) this.submitButton.disabled = true;
        });
        this.buttonObserver.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled'] });
      }

      this.syncVariantFromForm();
      this.clampQuantity();
      /* MARCA | 25 Sep 2026 | Two or three cases stay locked until every extra case is chosen. */
      this.syncAddLock();
    }

    disconnectedCallback() {
      this.dataset.bound = 'false';
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      this.cartBarButton?.removeEventListener(
        "click",
        this.onCartBarClick,
        true,
      );
      this.productInfo?.removeEventListener('change', this.onProductChange);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.variantObserver?.disconnect();
      this.buttonObserver?.disconnect();
    }

    fillPickers() {
      const template = this.querySelector('[data-picker-template]');
      if (!template) return;

      this.querySelectorAll('[data-menu]').forEach((menu) => {
        menu.replaceChildren(template.content.cloneNode(true));
      });
    }

    selectedCount() {
      const selected = this.querySelector('[data-purchase-option]:checked');
      return Number(selected?.value || 1);
    }

    selectedOption() {
      return this.querySelector('.purchase-options__option.is-selected');
    }

    handleChange(event) {
      const input = event.target.closest('[data-purchase-option]');
      if (!input) return;

      this.querySelectorAll('.purchase-options__option').forEach((option) => {
        option.classList.toggle('is-selected', option.contains(input));
      });

      this.closeMenus();

      if (input.value === '1') {
        this.syncQuantity();
      } else if (this.quantityInput) {
        this.quantityInput.value = '1';
      }

      this.updatePrices();
      this.syncAddLock();
    }

    handleClick(event) {
      const quantityButton = event.target.closest('[data-qty-change]');
      if (quantityButton) {
        event.preventDefault();
        this.changeQuantity(Number(quantityButton.dataset.qtyChange));
        return;
      }

      /* MARCA | 28 Sep 2026 | Each open picker switches its own collection tab. */
      const tab = event.target.closest('[data-picker-tab]');
      if (tab) {
        event.preventDefault();
        const menu = tab.closest('[data-menu]');
        const id = tab.dataset.pickerTab;
        if (!menu || !id) return;

        menu.querySelectorAll('[data-picker-tab]').forEach((button) => {
          const active = button === tab;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        menu.querySelectorAll('[data-picker-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.pickerPanel !== id;
        });
        return;
      }

      const choice = event.target.closest('[data-choice]');
      if (choice) {
        event.preventDefault();
        this.selectChoice(choice);
        return;
      }

      const opener = event.target.closest('[data-open-picker]');
      if (opener) {
        event.preventDefault();
        const slot = opener.closest('[data-slot]');
        if (slot) this.openPicker(slot);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') this.closeMenus();
    }

    changeQuantity(delta) {
      this.quantity = Math.min(this.maxQty, Math.max(1, this.quantity + delta));
      const value = this.querySelector('[data-qty-value]');
      if (value) value.textContent = String(this.quantity);
      this.syncQuantity();
      this.clampQuantity();
    }

    clampQuantity() {
      this.querySelectorAll('[data-qty-change]').forEach((button) => {
        const direction = Number(button.dataset.qtyChange);
        button.disabled = direction < 0 ? this.quantity <= 1 : this.quantity >= this.maxQty;
      });
    }

    syncQuantity() {
      if (this.quantityInput && this.selectedCount() === 1) {
        this.quantityInput.value = String(this.quantity);
      }
    }

    syncVariantFromForm() {
      const id = this.variantInput?.value;
      if (!id) return;

      const variant = this.variants.find((item) => String(item.id) === String(id));
      if (!variant) return;

      const changed = String(id) !== String(this.currentVariantId) || variant.price !== this.unitPrice;
      this.currentVariantId = String(id);
      this.unitPrice = variant.price;
      this.maxQty = variant.maxQty || 10;

      if (this.quantity > this.maxQty) {
        this.quantity = this.maxQty;
        const value = this.querySelector('[data-qty-value]');
        if (value) value.textContent = String(this.quantity);
        this.syncQuantity();
      }

      this.querySelectorAll('[data-current-price]').forEach((node) => {
        node.textContent = this.money(variant.price);
      });

      if (changed) this.updatePrices();
      this.clampQuantity();
      this.syncAddLock();
    }

    openPicker(slot) {
      const menu = slot.querySelector('[data-menu]');
      if (!menu) return;

      const willOpen = menu.hidden;
      this.closeMenus();

      if (!willOpen) return;

      menu.hidden = false;
      menu.scrollTop = 0;
      slot.classList.add('is-open');
      slot.querySelectorAll('[aria-expanded]').forEach((node) => node.setAttribute('aria-expanded', 'true'));

      /* MARCA | 29 Sep 2026 | Bring the whole picker on screen so the pointer is over it and it scrolls straight away. */
      requestAnimationFrame(() => menu.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
    }

    closeMenus() {
      this.querySelectorAll('[data-menu]').forEach((menu) => {
        menu.hidden = true;
      });
      this.querySelectorAll('[data-slot]').forEach((slot) => {
        slot.classList.remove('is-open');
        slot.querySelectorAll('[aria-expanded]').forEach((node) => node.setAttribute('aria-expanded', 'false'));
      });
    }

    selectChoice(choice) {
      if (choice.disabled) return;

      const slot = choice.closest('[data-slot]');
      const card = choice.querySelector('.purchase-options__case');
      const body = slot?.querySelector('[data-slot-body]');
      if (!slot || !card || !body) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'purchase-options__selected';
      button.dataset.openPicker = '';
      button.setAttribute('aria-expanded', 'false');
      button.appendChild(card.cloneNode(true));

      body.replaceChildren(button);
      slot.dataset.selectedVariant = choice.dataset.variantId;
      slot.dataset.selectedPrice = choice.dataset.price;
      this.closeMenus();
      this.updatePrices();
      this.syncAddLock();
    }

    isComplete(option) {
      const slots = [...option.querySelectorAll('[data-slot]')];
      return slots.length > 0 && slots.every((slot) => slot.dataset.selectedVariant);
    }

    updatePrices() {
      this.querySelectorAll('.purchase-options__option').forEach((option) => {
        const cases = Number(option.dataset.cases);
        const discount = Number(option.dataset.discount) || 0;
        this.renderOptionPrice(option, this.unitPrice * cases, discount);

        if (cases > 1) this.renderSummary(option);
      });
    }

    renderOptionPrice(option, original, discount) {
      const discounted = this.discounted(original, discount);
      const current = option.querySelector('[data-price-current]');
      const compare = option.querySelector('[data-price-compare]');
      const cases = Number(option.dataset.cases);

      if (current) current.textContent = this.money(discounted);
      if (compare) {
        /* MARCA | 24 Sep 2026 | One case can strike its price in the row. Two and three cases strike the total in the summary. */
        compare.hidden = !(
          cases === 1 &&
          discount > 0 &&
          discounted !== original
        );
        compare.textContent = this.money(original);
      }
    }

    renderSummary(option) {
      const slots = [...option.querySelectorAll('[data-slot]')];
      const filled = slots.filter((slot) => slot.dataset.selectedVariant);
      const count = 1 + filled.length;
      const original = this.unitPrice + filled.reduce((sum, slot) => sum + Number(slot.dataset.selectedPrice || 0), 0);
      const discount = Number(option.dataset.discount) || 0;
      const discounted = this.discounted(original, discount);

      const cans = option.querySelector('[data-summary-cans]');
      const cases = option.querySelector('[data-summary-cases]');
      const total = option.querySelector('[data-summary-total]');
      const compare = option.querySelector('[data-summary-compare]');
      const subtotal = option.querySelector('[data-summary-subtotal]');
      const badge = option.querySelector('[data-summary-off]');

      if (cans) cans.textContent = `(${this.cansPerCase * count} cans)`;
      if (cases) cases.textContent = count === 1 ? '1 CASE' : `${count} CASES`;
      if (subtotal) subtotal.textContent = this.money(discounted);

      const showDiscount = discount > 0 && discounted !== original;
      if (badge) {
        badge.hidden = !showDiscount;
        badge.textContent = `${discount}% OFF`;
      }
      if (compare) {
        compare.hidden = !showDiscount;
        compare.textContent = this.money(original);
      }
      if (total) {
        total.hidden = showDiscount;
        total.textContent = this.money(original);
      }
    }

    discounted(cents, percent) {
      const rate = Math.min(100, Math.max(0, Number(percent) || 0));
      return Math.round((Number(cents) * (100 - rate)) / 100);
    }

    money(cents) {
      const value = Math.round(Number(cents) || 0);
      const format = window.theme?.moneyWithoutCurrencyFormat || window.theme?.moneyFormat;

      if (window.theme?.formatMoney)
        return window.theme.formatMoney(value, format);
      return (value / 100).toFixed(2);
    }

    handleSubmit(event) {
      const cases = this.selectedCount();

      /* MARCA | 24 Sep 2026 | One case syncs the stepper into the product form and leaves the theme submit untouched. */
      if (cases <= 1) {
        this.syncQuantity();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (this.adding) return;

      const option = this.selectedOption();
      if (!option || !this.isComplete(option)) {
        this.syncAddLock();
        const emptySlot = [...(option?.querySelectorAll('[data-slot]') || [])].find((slot) => !slot.dataset.selectedVariant);
        if (emptySlot) this.openPicker(emptySlot);
        this.releaseCartBar();
        return;
      }

      const items = this.buildItems();
      if (!items.length) return;

      this.adding = true;
      this.clearError();
      this.setButtonLoading(true);

      const addUrl = window.theme.routes.cart_add_url.endsWith(".js")
        ? window.theme.routes.cart_add_url
        : `${window.theme.routes.cart_add_url}.js`;

      fetch(addUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ items }),
      })
        .then((response) =>
          response.json().then((data) => ({ ok: response.ok, data })),
        )
        .then(({ ok, data }) => {
          if (!ok || data?.status) {
            this.failAdd(data);
            return;
          }

          const hasCartUi =
            document.querySelector("cart-drawer") ||
            document.querySelector("[data-cart-page]");
          if (!hasCartUi || window.theme.settings.cartType === "page") {
            window.location = window.theme.routes.cart_url;
            return;
          }

          if (this.submitButton) {
            document.dispatchEvent(
              new CustomEvent("theme:product:add", {
                bubbles: true,
                detail: {
                  response: data,
                  button: this.submitButton,
                },
              }),
            );
          }
          document.dispatchEvent(
            new CustomEvent("theme:cart:refresh", { bubbles: true }),
          );
        })
        .catch((error) => {
          this.failAdd({
            message: error?.message || "Could not add these cases to the cart.",
          });
        })
        .finally(() => {
          this.adding = false;
          this.syncAddLock();
        });
    }

    /* MARCA | 25 Sep 2026 | Two or three cases lock add to cart until each extra case is chosen. */
    needsCases() {
      if (this.selectedCount() <= 1) return false;
      const option = this.selectedOption();
      return !option || !this.isComplete(option);
    }

    syncAddLock() {
      const locked = this.needsCases();
      this.locked = locked;
      this.setPurchaseLock(locked);

      if (locked) {
        this.warningVisible = true;
        this.showError(this.dataset.incompleteMessage || 'Select your cases, or choose 1 case.');
        return;
      }

      if (this.warningVisible) {
        this.warningVisible = false;
        this.clearError();
      }
    }

    setPurchaseLock(locked) {
      [this.submitButton, this.cartBarButton].forEach((button) => {
        if (!button) return;

        if (locked) {
          if (button.dataset.purchaseLock !== 'true') {
            button.dataset.purchaseWasDisabled = button.disabled ? 'true' : 'false';
            button.dataset.purchaseLock = 'true';
          }
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          return;
        }

        if (button.dataset.purchaseLock !== 'true') return;

        const wasDisabled = button.dataset.purchaseWasDisabled === 'true';
        delete button.dataset.purchaseLock;
        delete button.dataset.purchaseWasDisabled;
        button.disabled = wasDisabled;
        if (wasDisabled) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    }

    buildItems() {
      const currentId = Number(this.variantInput?.value || this.dataset.variantId);
      const option = this.selectedOption();
      const ids = [currentId];

      option.querySelectorAll('[data-slot]').forEach((slot) => {
        if (slot.dataset.selectedVariant) ids.push(Number(slot.dataset.selectedVariant));
      });

      const extras = this.readFormExtras();
      const lines = new Map();

      ids.forEach((id) => {
        if (!id) return;
        const existing = lines.get(id) || { id, quantity: 0 };
        existing.quantity += 1;

        if (id === currentId) {
          if (extras.properties && Object.keys(extras.properties).length) existing.properties = extras.properties;
          if (extras.sellingPlan) existing.selling_plan = Number(extras.sellingPlan) || extras.sellingPlan;
        }

        lines.set(id, existing);
      });

      return [...lines.values()];
    }

    readFormExtras() {
      const properties = {};
      let sellingPlan = null;

      if (!this.form) return { properties, sellingPlan };

      const data = new FormData(this.form);
      for (const [key, value] of data.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (match && value !== '') properties[match[1]] = value;
        if (key === 'selling_plan' && value) sellingPlan = value;
      }

      return { properties, sellingPlan };
    }

    setButtonLoading(isLoading) {
      if (this.submitButton) {
        this.submitButton.classList.toggle('is-loading', isLoading);
        this.submitButton.disabled = isLoading;
      }

      if (!this.cartBarButton) return;

      this.cartBarButton.classList.toggle('is-loading', isLoading);
      if (isLoading) this.cartBarButton.setAttribute('disabled', 'disabled');
      else this.cartBarButton.removeAttribute('disabled');
    }

    releaseCartBar() {
      if (!this.cartBarButton) return;
      this.cartBarButton.classList.remove('is-loading');
      this.cartBarButton.removeAttribute('disabled');
    }

    failAdd(data) {
      const message = data?.description && data.description !== data.message
        ? `${data.message}: ${data.description}`
        : data?.message || data?.description || 'Could not add these cases to the cart.';

      this.showError(message);
      this.setButtonLoading(false);
    }

    showError(message) {
      const error = this.querySelector('[data-error]');
      if (!error) return;
      error.hidden = false;
      error.textContent = message;
    }

    clearError() {
      const error = this.querySelector('[data-error]');
      if (!error) return;
      error.hidden = true;
      error.textContent = '';
    }
  }

  customElements.define('purchase-options', PurchaseOptions);
}
